import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Check, FileSearch, X } from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { GuideLink } from '../../guide/guide-link';
import {
  approveChangeRequest,
  listChangeRequests,
  rejectChangeRequest,
  type ChangeRequestListItem,
  type ChangeRequestStatus,
} from '../../orders/api';
import { mapOrderError } from '../../orders/order-api-errors';
import { hasPermission } from '../../auth/has-permission';

const changeRequestListRoute = getRouteApi('/protected/orders/change-requests');

const REQUEST_ADMIN_HINT = 'No podés aprobar tu propia solicitud.';

const STATUS_LABELS: Record<ChangeRequestStatus, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  APPLIED: 'Aplicada',
};

function formatDiff(request: ChangeRequestListItem): string {
  if (request.lineChanges.length === 0) {
    return '—';
  }

  return request.lineChanges
    .map((line) => `${line.positionName}: ${line.oldQuantity} → ${line.newQuantity}`)
    .join(', ');
}

/**
 * `order-change-requests/spec.md` → "Pantalla de bandeja de solicitudes
 * en back-office". `status` defaults to `PENDING` (`design.md` §8.2)
 * unless the actor explicitly asks for `'ALL'` or a terminal status —
 * that explicit choice is never overridden client-side (`tasks.md` PR 6
 * TRIANGULATE).
 */
export function ChangeRequestListPage(): JSX.Element {
  const search = changeRequestListRoute.useSearch();
  const navigate = changeRequestListRoute.useNavigate();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [approveTarget, setApproveTarget] = useState<ChangeRequestListItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ChangeRequestListItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const query = useQuery({
    queryKey: ['change-requests', search.status, search.page, search.pageSize],
    queryFn: () =>
      listChangeRequests({
        status: search.status === 'ALL' ? undefined : search.status,
        page: search.page,
        pageSize: search.pageSize,
      }),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['change-requests'] });
  }

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveChangeRequest(id),
    onSuccess: async () => {
      await invalidate();
      setApproveTarget(null);
      toast('success', 'Solicitud aprobada y aplicada');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectChangeRequest(id, reason),
    onSuccess: async () => {
      await invalidate();
      setRejectTarget(null);
      setRejectionReason('');
      toast('success', 'Solicitud rechazada');
    },
  });

  function changeStatusFilter(status: (typeof search)['status']): void {
    void navigate({ search: (prev) => ({ ...prev, status, page: 1 }) });
  }

  const currentUserId = currentUser.data?.id;
  const canApprove = hasPermission(currentUser.data, 'order.approve.change');

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={FileSearch}
        title="Solicitudes de cambio"
        description="Revisá y resolvé los pedidos de cambio sobre pedidos ya confirmados."
        actions={<GuideLink moduleId="orders" />}
      />

      <Card>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-slate-900">Estado</span>
          <select
            value={search.status}
            onChange={(event) =>
              changeStatusFilter(event.target.value as (typeof search)['status'])
            }
            className="h-11 w-full max-w-xs rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="PENDING">Pendientes</option>
            <option value="ALL">Todas</option>
            <option value="APPROVED">Aprobadas</option>
            <option value="REJECTED">Rechazadas</option>
            <option value="APPLIED">Aplicadas</option>
          </select>
        </label>
      </Card>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar las solicitudes">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.items.length === 0 ? (
        <EmptyState icon={FileSearch} title="No hay solicitudes de cambio para este filtro." />
      ) : null}

      {query.data !== undefined && query.data.items.length > 0 ? (
        <Table>
          <caption className="sr-only">Solicitudes de cambio</caption>
          <thead>
            <tr>
              <TableHeader>Pedido</TableHeader>
              <TableHeader>Solicitante</TableHeader>
              <TableHeader>Cambio solicitado</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {query.data.items.map((request) => {
              const isOwnRequest = request.requestedBy === currentUserId;
              const canResolve = canApprove && !isOwnRequest && request.status === 'PENDING';

              return (
                <TableRow key={request.id}>
                  <TableCell className="font-medium text-slate-900">
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: request.orderId }}
                      className="hover:underline"
                    >
                      {request.orderNumber}
                    </Link>
                  </TableCell>
                  <TableCell>{request.requesterFullName ?? request.requestedBy}</TableCell>
                  <TableCell>{formatDiff(request)}</TableCell>
                  <TableCell>{STATUS_LABELS[request.status]}</TableCell>
                  <TableCell>
                    {request.status === 'PENDING' ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={Check}
                          disabled={!canResolve}
                          title={isOwnRequest ? REQUEST_ADMIN_HINT : undefined}
                          onClick={() => {
                            approveMutation.reset();
                            setApproveTarget(request);
                          }}
                        >
                          Aprobar
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={X}
                          disabled={!canResolve}
                          title={isOwnRequest ? REQUEST_ADMIN_HINT : undefined}
                          onClick={() => setRejectTarget(request)}
                        >
                          Rechazar
                        </Button>
                        {isOwnRequest ? (
                          <span className="text-xs text-slate-500">{REQUEST_ADMIN_HINT}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      {approveTarget !== null ? (
        <Modal
          open
          onClose={() => setApproveTarget(null)}
          title="Aprobar solicitud de cambio"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {approveMutation.isError ? (
              <Alert tone="danger">{mapOrderError(approveMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-700">
              El cambio se aplica al pedido {approveTarget.orderNumber} ahora mismo. ¿Querés
              aprobarlo?
            </p>
            <div className="flex items-center gap-3">
              <Button
                loading={approveMutation.isPending}
                onClick={() => approveMutation.mutate(approveTarget.id)}
              >
                Confirmar aprobación
              </Button>
              <Button variant="secondary" onClick={() => setApproveTarget(null)}>
                Volver
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {rejectTarget !== null ? (
        <Modal
          open
          onClose={() => setRejectTarget(null)}
          title="Rechazar solicitud de cambio"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {rejectMutation.isError ? (
              <Alert tone="danger">{mapOrderError(rejectMutation.error)}</Alert>
            ) : null}
            <Field
              label="Motivo del rechazo"
              name="rejectionReason"
              required
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={rejectMutation.isPending}
                disabled={rejectionReason.trim() === ''}
                onClick={() =>
                  rejectMutation.mutate({ id: rejectTarget.id, reason: rejectionReason })
                }
              >
                Confirmar rechazo
              </Button>
              <Button variant="secondary" onClick={() => setRejectTarget(null)}>
                Volver
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
